'use client';

import { useCallback, useState } from 'react';

export type SiteChatMessage = {
  id: string;
  role: 'user' | 'assistant';
  content: string;
};

const STARTER: SiteChatMessage = {
  id: 'welcome',
  role: 'assistant',
  content:
    'Hi — I can help with BrainScraper pricing, how enrichment works, billing, webhooks, and where to find Terms or Privacy. What do you need?',
};

export function useSiteChat() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<SiteChatMessage[]>([STARTER]);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const send = useCallback(async (raw: string) => {
    const content = raw.trim().slice(0, 500);
    if (!content || pending) return;
    setError(null);
    const userMsg: SiteChatMessage = {
      id: `u-${Date.now()}`,
      role: 'user',
      content,
    };
    const next = [...messages, userMsg];
    setMessages(next);
    setPending(true);
    try {
      const res = await fetch('/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages: next
            .filter((m) => m.id !== 'welcome')
            .slice(-8)
            .map((m) => ({ role: m.role, content: m.content })),
        }),
      });
      const body = await res.json();
      if (!res.ok || !body.success) {
        throw new Error(body.error || 'The assistant is unavailable.');
      }
      setMessages((prev) => [
        ...prev,
        {
          id: `a-${Date.now()}`,
          role: 'assistant',
          content: String(body.message || ''),
        },
      ]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The assistant is unavailable.');
    } finally {
      setPending(false);
    }
  }, [messages, pending]);

  return { open, setOpen, messages, pending, error, send };
}
